import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * HoverTip — tooltip da plataforma para elementos marcados com `data-tip`.
 *
 * O `title` nativo do navegador tem visual próprio, demora ~1s e não segue o
 * estilo do sistema. Envolver dezenas de elementos com o `Tooltip` do Radix
 * (um provider por item) pesaria nas listas grandes do Painel Logístico, então
 * aqui há UM único listener delegado no documento: qualquer elemento com
 * `data-tip="texto"` ganha a dica no mesmo visual do resto do app.
 *
 * Uso: <button data-tip="Filtrar por tag"> — nada mais.
 * Montado uma vez em `App.js`.
 */
const DELAY_MS = 150;
const MARGEM = 8;

export default function HoverTip() {
  const [tip, setTip] = useState(null); // { texto, x, y, acima }
  const [pos, setPos] = useState(null); // { left, top } já ajustado à viewport
  const ref = useRef(null);

  useEffect(() => {
    let timer = null;
    let alvoAtual = null;

    const esconder = () => {
      clearTimeout(timer);
      alvoAtual = null;
      setTip(null);
      setPos(null);
    };

    const mostrar = (alvo) => {
      const texto = alvo.getAttribute("data-tip");
      if (!texto) return;
      const r = alvo.getBoundingClientRect();
      const acima = r.top > 120;
      setTip({
        texto,
        x: r.left + r.width / 2,
        y: acima ? r.top - MARGEM : r.bottom + MARGEM,
        acima,
      });
    };

    const onOver = (e) => {
      const alvo = e.target?.closest?.("[data-tip]");
      if (!alvo || alvo === alvoAtual) return;
      clearTimeout(timer);
      alvoAtual = alvo;
      timer = setTimeout(() => {
        // O ponteiro pode ter saído durante o atraso.
        if (alvoAtual === alvo && document.contains(alvo)) mostrar(alvo);
      }, DELAY_MS);
    };

    const onOut = (e) => {
      const alvo = e.target?.closest?.("[data-tip]");
      if (!alvo) return;
      if (e.relatedTarget?.closest?.("[data-tip]") === alvo) return;
      esconder();
    };

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    document.addEventListener("mousedown", esconder, true);
    window.addEventListener("scroll", esconder, true);
    window.addEventListener("blur", esconder);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      document.removeEventListener("mousedown", esconder, true);
      window.removeEventListener("scroll", esconder, true);
      window.removeEventListener("blur", esconder);
    };
  }, []);

  // A dica é medida já renderizada (invisível) e só então posicionada: o texto
  // tem largura variável, então sem medir ela escapa da borda da tela.
  useLayoutEffect(() => {
    if (!tip) return;
    const el = ref.current;
    if (!el) return;
    const { width: w, height: h } = el.getBoundingClientRect();
    const maxX = window.innerWidth - w - MARGEM;
    const left = Math.max(MARGEM, Math.min(tip.x - w / 2, Math.max(MARGEM, maxX)));
    let top = tip.acima ? tip.y - h : tip.y;
    if (top < MARGEM) top = tip.y + MARGEM; // sem espaço acima: cai para baixo
    if (top + h > window.innerHeight - MARGEM) {
      top = Math.max(MARGEM, window.innerHeight - h - MARGEM);
    }
    setPos({ left, top });
  }, [tip]);

  if (!tip) return null;

  return (
    <div
      ref={ref}
      role="tooltip"
      data-testid="hover-tip"
      className="fixed pointer-events-none rounded-md px-3 py-1.5 text-xs text-white max-w-[320px] animate-in fade-in-0 zoom-in-95"
      style={{
        left: pos ? pos.left : tip.x,
        top: pos ? pos.top : tip.y,
        visibility: pos ? "visible" : "hidden",
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        zIndex: 99999,
        whiteSpace: "pre-line",
      }}
    >
      {tip.texto}
    </div>
  );
}
